import type { ApiError, ErrorCode } from '@riba/shared';
import { useAuthStore } from '../auth/auth.store';

const BASE = '/api/v1';

/** A failed API call, carrying the backend's stable machine-readable code. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the Authorization header (login). */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/**
 * The one place that talks to the API.
 *
 * Staff tokens are 12h and non-refreshable by design (MoySklad credentials,
 * mirroring the WMS), so a 401 means "log in again" — we clear the session and
 * let the route guard bounce to /login rather than attempting a silent refresh.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous } = options;
  const token = useAuthStore.getState().token;

  const res = await fetch(buildUrl(path, query), {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(!anonymous && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !anonymous) {
    useAuthStore.getState().clear();
  }

  if (res.status === 204) return undefined as T;

  const payload = (await res.json().catch(() => null)) as ApiError | T | null;

  if (!res.ok) {
    const err = payload as ApiError | null;
    throw new ApiRequestError(
      err?.code ?? 'internal_error',
      res.status,
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
    );
  }

  return payload as T;
}
